import React, { useState, useEffect, useRef } from 'react';
import { Modal, Progress, Button, Typography, Result } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import socketInstance from '../services/socket';
import { exportApi } from '../services/actions/export';

const { Text } = Typography;

const ExportModal = ({ user, visible, onClose, queueId, exportResult }) => {
  // Local state for progress and status only
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [message, setMessage] = useState('Iniciando exportação...');
  const socketRef = useRef(null);
  const pollRef = useRef(null);
  const hasSocketProgressRef = useRef(false);

  // Refs to access latest state inside socket listeners without re-running effect
  const visibleRef = useRef(visible);
  const progressRef = useRef(progress);

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setProgress(0);
      setStatus('processing');
      setDownloadUrl(null);
      setMessage('Aguardando inicio do processamento...');
    }
  }, [visible]);

  // Guard: never allow progress to go backwards on screen
  useEffect(() => {
    setProgress(prev => (progress < prev ? prev : progress));
  }, [progress]);

  // Fallback: update UI when API returns (even if websocket isn't connected)
  useEffect(() => {
    if (!visible || !exportResult) return;
    if (exportResult.status === 'success') {
      setProgress(100);
      setStatus('success');
      setDownloadUrl(exportResult.downloadUrl || null);
      setMessage(exportResult.message || 'Sua exportacao esta pronta!');
    } else if (exportResult.status === 'error') {
      setStatus('error');
      setMessage(exportResult.message || 'Erro ao processar exportacao.');
    }
  }, [exportResult, visible]);

  const normalizeProgress = (rawValue) => {
    if (rawValue === null || rawValue === undefined) return null;
    if (typeof rawValue === 'string') {
      const cleaned = rawValue.replace('%', '').trim();
      const parsed = Number(cleaned);
      if (!Number.isFinite(parsed)) return null;
      return (parsed > 0 && parsed < 1) ? Math.round(parsed * 100) : parsed;
    }
    if (typeof rawValue === 'number') {
      if (!Number.isFinite(rawValue)) return null;
      return (rawValue > 0 && rawValue < 1) ? Math.round(rawValue * 100) : rawValue;
    }
    return null;
  };

  const clampPercent = (value) => Math.max(0, Math.min(100, value));

  useEffect(() => {
    if (!visible || !queueId) return;

    let active = true;

    const applyProgressPayload = (payload) => {
      if (!payload) return;
      if (hasSocketProgressRef.current) return;
      const nextProgress = normalizeProgress(payload.progress);
      if (nextProgress !== null) {
        const currentProgress = progressRef.current ?? 0;
        if (nextProgress >= currentProgress) {
          setProgress(clampPercent(nextProgress));
        }
      }
      if (payload.message) {
        setMessage(payload.message);
      }
      if (payload.status === 'success') {
        setProgress(100);
        setStatus('success');
        setDownloadUrl(payload.downloadUrl || null);
      } else if (payload.status === 'error') {
        setStatus('error');
      }
    };

    const pollProgress = async () => {
      try {
        if (hasSocketProgressRef.current) return;
        const response = await exportApi.getProgress(queueId);
        if (!active) return;
        const body = response?.data;
        if (body?.success === false) return;
        const payload = body?.progress || body?.data || body;
        applyProgressPayload(payload);
        if (payload?.status === 'success' || payload?.status === 'error') {
          if (pollRef.current) clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (error) {
        // Ignore polling errors to avoid console noise when endpoint is unavailable
      }
    };

    pollProgress();
    pollRef.current = setInterval(pollProgress, 1500);

    return () => {
      active = false;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [visible, queueId]);

  useEffect(() => {
    if (!user?.sub) return;

    let active = true;

    const handleConnect = () => {
      setMessage('Conectado! Aguardando dados...');
      setProgress(prev => prev);
    };

    const handleDisconnect = (reason) => {
      setMessage('Desconectado. Tentando reconectar...');
    };

    const handleConnectError = (err) => {
      setMessage(`Erro de conexão: ${err.message}`);
    };


    const handleProgress = (data) => {
      if (!data) return;

      // Strict check: if we have a queueId, the event MUST match it
      if (queueId && data.queueId !== queueId) return;

      if (!hasSocketProgressRef.current) {
        hasSocketProgressRef.current = true;
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }

      const nextProgress = normalizeProgress(
        data.progress ??
        data.percentage ??
        data.percent ??
        data.progressPercent ??
        data.pct
      );
      if (nextProgress !== null) {
        const currentProgress = progressRef.current ?? 0;
        if (nextProgress < currentProgress) {
          return;
        } else {
          setProgress(clampPercent(nextProgress));
        }
      }
      setStatus('processing');
      setMessage(data.message || data.statusMessage || data.status || 'Processando...');
    };

    const handleNotification = (data) => {
      const shouldShow = visibleRef.current || progressRef.current > 0;

      if (data?.type === 'success' || data?.type === 'info') {
        if (data?.action && shouldShow) {
          setProgress(100);
          setStatus('success');
          setDownloadUrl(data.action);
          setMessage('Sua exportaÃ§Ã£o estÃ¡ pronta!');
        }
      } else if (data?.type === 'error') {
        if (shouldShow) {
          setStatus('error');
          setMessage('Erro ao processar exportaÃ§Ã£o.');
        }
      }
    };

    const matchesPayload = (data) => {
      if (!data) return false;
      if (queueId && data.queueId !== queueId) return false;
      if (data.userId && data.userId !== user.sub) return false;
      if (data.user && data.user !== user.sub) return false;
      if (data.sub && data.sub !== user.sub) return false;
      return true;
    };

    const handleProgressGeneric = (data) => {
      if (!matchesPayload(data)) return;
      handleProgress(data);
    };

    const initializeSocket = async () => {
      const socket = await socketInstance();
      if (!socket || !active) return;

      socketRef.current = socket;

      socket.emit('join', 'export');
      if (queueId) socket.emit('join', queueId);
      socket.on('connect', handleConnect);
      socket.on('disconnect', handleDisconnect);
      socket.on('connect_error', handleConnectError);

      const userProgressEvent = `${user.sub}:exportProgress`;
      const queueProgressEvent = queueId ? `${queueId}:exportProgress` : null;

      socket.on(userProgressEvent, handleProgress);
      if (queueProgressEvent) {
        socket.on(queueProgressEvent, handleProgress);
      }
      socket.on('exportProgress', handleProgressGeneric);
      socket.on('export:progress', handleProgressGeneric);
      socket.on(`${user.sub}:newNotification`, handleNotification);
    };

    initializeSocket();

    return () => {
      active = false;
      if (!socketRef.current) return;

      socketRef.current.off('connect', handleConnect);
      socketRef.current.off('disconnect', handleDisconnect);
      socketRef.current.off('connect_error', handleConnectError);
      const userProgressEvent = `${user.sub}:exportProgress`;
      const queueProgressEvent = queueId ? `${queueId}:exportProgress` : null;

      socketRef.current.off(userProgressEvent, handleProgress);
      if (queueProgressEvent) {
        socketRef.current.off(queueProgressEvent, handleProgress);
      }
      socketRef.current.off('exportProgress', handleProgressGeneric);
      socketRef.current.off('export:progress', handleProgressGeneric);
      socketRef.current.off(`${user.sub}:newNotification`, handleNotification);
    };
  }, [user?.sub, queueId]);

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    // Optional: Reset internal state here too if desired, though useEffect[visible] handles it on re-open
    setProgress(0);
    setStatus('processing');
    setDownloadUrl(null);
    setMessage('Iniciando exportação...');
  };

  return (
    (<Modal
      open={visible}
      title="Exportação de Dados"
      footer={null}
      onCancel={handleClose}
      closable={status !== 'processing'}
      maskClosable={false}
      keyboard={false}
    >
      <div style={{ textAlign: 'center', padding: '20px' }}>
        {status === 'processing' && (
          <>
            <Progress type="circle" percent={progress} strokeColor="#12B398" />
            <div style={{ marginTop: '20px' }}>
              <Text>{message}</Text>
            </div>
            <div style={{ marginTop: '20px', color: 'red' }}>
              <ExclamationCircleOutlined style={{ marginRight: '8px' }} />
              <Text type="danger">Não feche a guia, senão a exportação será interrompida</Text>
            </div>
          </>
        )}

        {status === 'success' && (
          <Result
            status="success"
            title="Exportação Concluída!"
            subTitle="Seu arquivo está pronto para download."
            extra={[
              <Button type="primary" key="download" onClick={handleDownload}>
                Baixar Arquivo
              </Button>,
              <Button key="close" onClick={handleClose}>
                Fechar
              </Button>,
            ]}
          />
        )}

        {status === 'error' && (
          <Result
            status="error"
            title="Erro na Exportação"
            subTitle={message || "Ocorreu um erro ao processar sua solicitação."}
            extra={[
              <Button key="close" onClick={handleClose}>
                Fechar
              </Button>,
            ]}
          />
        )}
      </div>
    </Modal>)
  );
};

const mapStateToProps = ({ session }) => ({
  user: session.user,
});

export default connect(mapStateToProps)(ExportModal);
